import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet-polylinedecorator'
import 'leaflet-gpx'
import 'iconify-icon'

/**
 * Race detail page: Leaflet map + GPX track + direction arrows + one-shot route animation.
 */
;(function () {
  var el = document.getElementById('race-gpx-map')
  if (!el) {
    return
  }

  var url = el.getAttribute('data-gpx-url')
  if (!url) {
    return
  }

  var map = L.map(el, { scrollWheelZoom: false })
  // Carto OSM raster tiles — tile.openstreetmap.org often returns 403 (referer /
  // usage policy) for embedded sites; Carto is a standard Leaflet-friendly option.
  L.tileLayer(
    'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
    {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: 'abcd',
      maxZoom: 20,
    },
  ).addTo(map)

  // Timotion $primary / $primary-700 (scss/colors.scss + primitives)
  var PRIMARY = '#f15d25'
  var PRIMARY_700 = '#de3e01'

  var startIcon = L.divIcon({
    className: 'race-gpx-marker race-gpx-marker--start',
    html:
      '<div class="icon is-icon-24 has-text-primary" >' +
      '<iconify-icon icon="ph:hexagon-fill"  aria-hidden="true"></iconify-icon></div>',
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  })

  function flattenLatLngs(latlngs) {
    var out = []
    function walk(ll) {
      if (!ll || ll.length === 0) {
        return
      }
      if (ll[0] instanceof L.LatLng) {
        for (var i = 0; i < ll.length; i++) {
          out.push(ll[i])
        }
      } else {
        for (var j = 0; j < ll.length; j++) {
          walk(ll[j])
        }
      }
    }
    walk(latlngs)
    return out
  }

  function collectTrackPoints(layer, acc) {
    if (layer instanceof L.Polyline && !(layer instanceof L.Polygon)) {
      var flat = flattenLatLngs(layer.getLatLngs())
      for (var i = 0; i < flat.length; i++) {
        acc.push(flat[i])
      }
    } else if (layer.eachLayer) {
      layer.eachLayer(function (child) {
        collectTrackPoints(child, acc)
      })
    }
  }

  function buildDistanceIndex(latlngs) {
    var segments = []
    var total = 0
    for (var i = 0; i < latlngs.length - 1; i++) {
      var len = latlngs[i].distanceTo(latlngs[i + 1])
      segments.push({ from: i, len: len, startDist: total })
      total += len
    }
    return { segments: segments, totalDistance: total }
  }

  function positionAtRatio(latlngs, segments, totalDistance, t) {
    if (!latlngs.length) {
      return null
    }
    if (latlngs.length === 1) {
      return latlngs[0]
    }
    if (totalDistance <= 0) {
      return latlngs[0]
    }
    var target = Math.max(0, Math.min(1, t)) * totalDistance
    var acc = 0
    for (var i = 0; i < segments.length; i++) {
      var seg = segments[i]
      if (acc + seg.len >= target) {
        var ratio = seg.len > 0 ? (target - acc) / seg.len : 0
        var a = latlngs[seg.from]
        var b = latlngs[seg.from + 1]
        return L.latLng(
          a.lat + ratio * (b.lat - a.lat),
          a.lng + ratio * (b.lng - a.lng),
        )
      }
      acc += seg.len
    }
    return latlngs[latlngs.length - 1]
  }

  function addDirectionArrows(layer) {
    if (layer instanceof L.Polyline && !(layer instanceof L.Polygon)) {
      if (typeof L.polylineDecorator === 'function') {
        L.polylineDecorator(layer, {
          patterns: [
            {
              offset: '6%',
              repeat: '14%',
              symbol: L.Symbol.arrowHead({
                pixelSize: 11,
                polygon: false,
                pathOptions: {
                  stroke: true,
                  color: PRIMARY_700,
                  weight: 2,
                  lineCap: 'round',
                  lineJoin: 'round',
                },
              }),
            },
          ],
        }).addTo(map)
      }
    } else if (layer.eachLayer) {
      layer.eachLayer(addDirectionArrows)
    }
  }

  /** One full run from start to finish (ms); runs once, then marker stays at end. */
  var ROUTE_AUTOPLAY_DURATION_MS = 15000

  function setupRouteSimulator(gpxLayer) {
    var path = []
    collectTrackPoints(gpxLayer, path)
    if (path.length < 2) {
      return
    }

    var idx = buildDistanceIndex(path)
    var simIcon = L.divIcon({
      className: 'race-gpx-sim-marker',
      html:
        '<div style="width:18px;height:18px;background:#fff;border:3px solid ' +
        PRIMARY +
        ';border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,.35);"></div>',
      iconSize: [18, 18],
      iconAnchor: [9, 9],
    })

    var simMarker = L.marker(
      positionAtRatio(path, idx.segments, idx.totalDistance, 0),
      {
        icon: simIcon,
        zIndexOffset: 2000,
      },
    ).addTo(map)

    var autoplayStartMs = performance.now()

    function autoplayTick(now) {
      var elapsed = now - autoplayStartMs
      var t = Math.min(1, elapsed / ROUTE_AUTOPLAY_DURATION_MS)
      var ll = positionAtRatio(
        path,
        idx.segments,
        idx.totalDistance,
        t,
      )
      if (ll) {
        simMarker.setLatLng(ll)
      }
      if (t < 1) {
        requestAnimationFrame(autoplayTick)
      }
    }

    requestAnimationFrame(autoplayTick)
  }

  new L.GPX(url, {
    async: true,
    gpx_options: { parseElements: ['track', 'route'] },
    polyline_options: { color: PRIMARY, weight: 4, opacity: 0.9 },
    marker_options: {
      startIcon: startIcon,
      endIconUrl: '',
      clickable: false,
      wptIconUrls: {},
      wptIconTypeUrls: {},
    },
  })
    .on('loaded', function (e) {
      var gpxLayer = e.target
      map.fitBounds(gpxLayer.getBounds())
      gpxLayer.eachLayer(addDirectionArrows)
      setupRouteSimulator(gpxLayer)
    })
    .on('error', function (e) {
      console.error('GPX load error', e)
    })
    .addTo(map)
})()

/**
 * Race list cards (`box_race_horizontal`): small preview map when no header image.
 */
;(function initRaceBoxMiniMaps() {
  var nodes = document.querySelectorAll('.race-box-mini-map[data-gpx-url]')
  if (!nodes.length) {
    return
  }

  var emptyMarker = L.divIcon({
    className: 'race-box-mini-map-no-marker',
    html: '',
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  })

  var tileOpts = {
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    subdomains: 'abcd',
    maxZoom: 20,
  }

  function invalidateLater(m) {
    setTimeout(function () {
      m.invalidateSize()
    }, 0)
    setTimeout(function () {
      m.invalidateSize()
    }, 250)
  }

  for (var i = 0; i < nodes.length; i++) {
    ;(function (el) {
      var url = el.getAttribute('data-gpx-url')
      if (!url) {
        return
      }
      var map = L.map(el, { scrollWheelZoom: false })
      L.tileLayer(
        'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
        tileOpts,
      ).addTo(map)

      new L.GPX(url, {
        async: true,
        gpx_options: { parseElements: ['track', 'route'] },
        polyline_options: { color: '#f15d25', weight: 3, opacity: 0.9 },
        marker_options: {
          startIcon: emptyMarker,
          endIconUrl: '',
          clickable: false,
          wptIconUrls: {},
          wptIconTypeUrls: {},
        },
      })
        .on('loaded', function (e) {
          var layer = e.target
          var b = layer.getBounds && layer.getBounds()
          if (b && b.isValid()) {
            map.fitBounds(b, { padding: [10, 10], maxZoom: 15 })
          }
          invalidateLater(map)
        })
        .on('error', function (err) {
          console.error('Race box GPX load error', err)
        })
        .addTo(map)
    })(nodes[i])
  }
})()
